<template>
    <div class="product-purchased-goal-modal">
        <GoalModal
            :is-open="isOpen"
            :summary-text="$t('campaign.product.purchased.summaryText')"
            :title="$t('campaign.product.purchased.title')"
            @update-config="updateConfig"
            @close-modal="closeModal"
        >
            <template #goalParameters>
                <section class="purchase-type">
                    <DsMultiselect
                        v-model="selectedPurchaseType"
                        bind-value-only
                        required
                        value-prop="id"
                        class="selected-purchase-type"
                        :placeholder="$t('campaign.product.purchased.purchaseTypePlaceholder')"
                        :options="availablePurchaseTypes"
                        :force-invalid="emptyPurchaseType"
                        @input="selectPaymentOption"
                    >
                        <template #option="{ option, highlight, dense, isSelected }">
                            <div
                                :data-qa="option.label"
                                class="menu-item"
                                :class="[{
                                    highlight,
                                    dense,
                                    selected: isSelected
                                }]"
                            >
                                <div>
                                    <span>{{ option.label }}</span>
                                    <br />
                                    <span v-if="option.helpText" class="option-subtext">{{ option.helpText }}</span>
                                </div>
                            </div>
                        </template>
                        <template #help>
                            <span v-if="emptyPurchaseType" data-qa="invalid-purchase-type">
                                {{ $t('global.field.required') }}
                            </span>
                        </template>
                    </DsMultiselect>
                </section>

                <section v-if="isOrderForm" class="order-forms">
                    <DsMultiselect
                        :key="selectedPurchaseType"
                        v-model="selectedOrderForms"
                        bind-value-only
                        multiple
                        searchable
                        required
                        class="selected-order-forms"
                        :readonly="loading"
                        :loading="loading"
                        :placeholder="$t('campaign.product.purchased.orderFormPlaceholder')"
                        :options="orderForms"
                        :force-invalid="emptyOrderForm"
                    >
                        <template v-if="emptyOrderForm" #help>
                            {{ $t('global.field.required') }}
                        </template>
                    </DsMultiselect>
                </section>

                <section v-if="isProduct" class="products">
                    <CustomMultiselect
                        :key="selectedPurchaseType"
                        v-model="selectedProducts"
                        bind-value-only
                        categorized
                        multiple
                        searchable
                        custom-chip-label
                        help-text
                        help-text-prop="subText"
                        label-prop="label"
                        value-prop="value"
                        filter-categories
                        remove-selected-categories
                        class="selected-products"
                        required
                        :readonly="loading"
                        :loading="loading"
                        :placeholder="$t('campaign.product.purchased.productPlaceholder')"
                        :options="products"
                        :force-invalid="emptyProduct"
                    >
                        <template #selection="{ option, remove }">
                            <DsChip
                                remove
                                with-interaction
                                @mousedown.native.stop.prevent="remove"
                            >
                                <template #default>
                                    {{ option.chipLabel || option.label }}
                                </template>
                            </DsChip>
                        </template>
                        <template v-if="emptyProduct" #help>
                            {{ $t('global.field.required') }}
                        </template>
                    </CustomMultiselect>
                </section>

                <section v-if="isProductCategory" class="product-categories">
                    <DsMultiselect
                        :key="selectedPurchaseType"
                        v-model="selectedProductCategories"
                        bind-value-only
                        multiple
                        searchable
                        required
                        class="selected-product-categories"
                        :readonly="loading"
                        :loading="loading"
                        :placeholder="$t('campaign.product.purchased.productCategoryPlaceholder')"
                        :options="productCategories"
                        :force-invalid="emptyProductCategory"
                    >
                        <template v-if="emptyProductCategory" #help>
                            {{ $t('global.field.required') }}
                        </template>
                    </DsMultiselect>
                </section>

                <section v-if="showPaymentTypes" class="payment-type">
                    <div class="payment-summary">
                        {{ $t('campaign.product.purchased.paymentTypeSummary') }}
                    </div>
                    <DsMultiselect
                        :key="selectedPurchaseType"
                        v-model="selectedPaymentTypes"
                        bind-value-only
                        multiple
                        searchable
                        help-text
                        required
                        help-text-prop="subText"
                        class="selected-payment-type"
                        :readonly="loading"
                        :loading="loading"
                        :options="paymentTypes"
                        :force-invalid="emptyPaymentType"
                        :placeholder="$t('campaign.product.purchased.paymentTypePlaceholder')"
                    >
                        <template #selection="{ option, remove }">
                            <DsChip
                                remove
                                with-interaction
                                @mousedown.native.stop.prevent="remove"
                            >
                                <template #default>
                                    {{ option.chipLabel || option.label }}
                                </template>
                            </DsChip>
                        </template>
                        <template v-if="emptyPaymentType" #help>
                            {{ $t('global.field.required') }}
                        </template>
                    </DsMultiselect>
                </section>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import CustomMultiselect from '@/components/shared/CustomMultiselect.vue';
import {
    loadPaymentTypes,
    loadProductList,
    loadOrderForms,
    loadProductCategories,
} from './purchase.api';

export const PURCHASE_TYPE_IDS = {
    orderForm: 'orderForm',
    product: 'product',
    category: 'category',
    all: 'all',
};

export const DEFAULT_PAYMENT_TYPES = [
    'Credit Card',
    'Include $0 invoices',
];

export default {
    PURCHASE_TYPE_IDS,
    DEFAULT_PAYMENT_TYPES,

    components: {
        GoalModal,
        CustomMultiselect,
    },

    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },

        appInfo: {
            type: Object,
            required: true,
        },

        cell: {
            type: Object,
            default: () => ({
                value: {
                    purchaseType: '',
                    paymentTypes: [],
                    purchaseOption: [],
                },
            }),
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            purchaseTypes: [
                {
                    id: PURCHASE_TYPE_IDS.orderForm,
                    label: this.$t('campaign.product.purchased.orderFormLabel'),
                    helpText: this.$t('campaign.product.purchased.orderFormHelpText'),
                }, {
                    id: PURCHASE_TYPE_IDS.product,
                    label: this.$t('campaign.product.purchased.productLabel'),
                    helpText: this.$t('campaign.product.purchased.productHelpText'),
                }, {
                    id: PURCHASE_TYPE_IDS.category,
                    label: this.$t('campaign.product.purchased.productCategoryLabel'),
                    helpText: this.$t('campaign.product.purchased.productCategoryHelpText'),
                }, {
                    id: PURCHASE_TYPE_IDS.all,
                    label: this.$t('campaign.product.purchased.anyLabel'),
                    helpText: this.$t('campaign.product.purchased.anyHelpText'),
                },
            ],
            purchaseTypesProMax: [
                {
                    id: PURCHASE_TYPE_IDS.product,
                    label: this.$t('campaign.product.purchased.productLabel'),
                    helpText: this.$t('campaign.product.purchased.productHelpText'),
                }, {
                    id: PURCHASE_TYPE_IDS.all,
                    label: this.$t('campaign.product.purchased.anyLabel'),
                    helpText: this.$t('campaign.product.purchased.anyHelpText'),
                },
            ],
            goalOptions: null,
            selectedPaymentTypes: [],
            selectedPurchaseType: '',
            paymentTypes: [],
            orderForms: [],
            selectedOrderForms: [],
            products: [],
            selectedProducts: [],
            productCategories: [],
            selectedProductCategories: [],
            loading: false,
            purchaseOptionId: null,
            emptyPurchaseType: false,
            emptyOrderForm: false,
            emptyProduct: false,
            emptyProductCategory: false,
            emptyPaymentType: false,
        };
    },

    computed: {
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },

        isOrderForm() {
            return this.selectedPurchaseType === this.$options.PURCHASE_TYPE_IDS.orderForm;
        },

        isProduct() {
            return this.selectedPurchaseType === this.$options.PURCHASE_TYPE_IDS.product;
        },

        isProductCategory() {
            return this.selectedPurchaseType === this.$options.PURCHASE_TYPE_IDS.category;
        },

        showPaymentTypes() {
            return !this.isOrderForm;
        },

        purchaseId() {
            let purchaseId = 0;

            if (this.isOrderForm) {
                purchaseId = this.selectedOrderForms;
            } else if (this.isProduct) {
                purchaseId = this.selectedProducts;
            } else if (this.isProductCategory) {
                purchaseId = this.selectedProductCategories;
            }

            return purchaseId;
        },

        availablePurchaseTypes() {
            return this.isSpaApp ? this.purchaseTypesProMax : this.purchaseTypes;
        },

        configured() {
            if (!this.selectedPurchaseType) {
                return false;
            }

            if ((this.isOrderForm && this.selectedOrderForms.length < 1)
                    || (this.isProduct && this.selectedProducts.length < 1)
                    || (this.isProductCategory && this.selectedProductCategories.length < 1)) {
                return false;
            }

            return !(!this.isOrderForm && this.selectedPaymentTypes.length < 1);
        },
    },

    async mounted() {
        await this.load();
    },

    methods: {
        async load() {
            try {
                this.loading = true;
                this.paymentTypes = await loadPaymentTypes();
                this.products = await loadProductList();

                if (!this.isSpaApp) {
                    this.productCategories = await loadProductCategories();
                    this.orderForms = await loadOrderForms();
                }

                this.loadCellValue();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Could not load product purchased: ${error}`);
            } finally {
                this.loading = false;
            }
        },

        loadCellValue() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};

            const purchaseIdValue = cell.value.purchaseId;
            const paymentTypeValues = cell.value.paymentTypes;
            const purchaseTypeValue = cell.value.purchaseType;

            this.purchaseOptionId = cell.value.purchaseOption;

            // load payment & purchase types
            if (!paymentTypeValues) {
                this.loadDefaultPaymentTypes();
            } else {
                this.selectedPaymentTypes = paymentTypeValues.split(',');
            }

            this.selectedPurchaseType = purchaseTypeValue;

            // load purchase ID(s)
            let purchaseIds;

            if (Array.isArray(purchaseIdValue)) {
                purchaseIds = purchaseIdValue.map((purchaseId) => {
                    const strPurchaseId = purchaseId.toString();

                    if (this.isProduct && this.hasGroups(strPurchaseId)) {
                        return this.getPurchaseOptionId(strPurchaseId);
                    }

                    return strPurchaseId;
                });
            } else if (purchaseIdValue) {
                const sanitizedPurchaseId = purchaseIdValue.toString().replace('L', '');

                if (this.isProduct && this.hasGroups(sanitizedPurchaseId)) {
                    purchaseIds = [this.getPurchaseOptionId(sanitizedPurchaseId)];
                } else {
                    purchaseIds = [sanitizedPurchaseId];
                }
            }

            if (this.isOrderForm) {
                if (this.isSpaApp) {
                    this.selectedPurchaseType = this.$options.PURCHASE_TYPE_IDS.all;
                    this.loadDefaultPaymentTypes();
                } else {
                    this.selectedOrderForms = purchaseIds.map((purchaseId) => Number(purchaseId));
                }
            }

            if (this.isProduct) {
                this.selectedProducts = purchaseIds;
            } else if (this.isProductCategory) {
                this.selectedProductCategories = purchaseIds.map((purchaseId) => Number(purchaseId));
            }
        },

        saveCellValue() {
            if (this.configured) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};

                const { purchaseId: purchaseIdValues } = this;

                if (Array.isArray(purchaseIdValues)) {
                    const purchaseOptionIds = [];
                    const purchaseIds = purchaseIdValues.map((purchaseId) => {
                        const strPurchaseId = purchaseId.toString();

                        if (strPurchaseId.includes(':')) {
                            const tokens = strPurchaseId.split(':');

                            purchaseOptionIds.push(tokens[1]);

                            return strPurchaseId;
                        }

                        return purchaseId;
                    });

                    cell.value.purchaseId = purchaseIds;
                    cell.value.eventId = purchaseIds;

                    cell.value.purchaseOption = purchaseOptionIds ?? 0;
                } else {
                    cell.value.purchaseId = purchaseIdValues;
                    cell.value.eventId = purchaseIdValues;

                    cell.value.purchaseOption = (this.purchaseOptionId) ? this.purchaseOptionId : ['0'];
                }

                cell.value.purchaseType = this.selectedPurchaseType;

                if (this.isOrderForm) {
                    this.loadDefaultPaymentTypes();
                }

                cell.value.paymentTypes = this.selectedPaymentTypes.toString();

                this.$emit('update-cell-value', { cell });
            }
        },

        updateConfig() {
            if (this.isValid()) {
                this.saveCellValue();
            }
        },

        isValid() {
            if (!this.selectedPurchaseType) {
                this.emptyPurchaseType = true;

                return false;
            }

            if (this.isOrderForm && this.selectedOrderForms.length < 1) {
                this.emptyOrderForm = true;
            }

            if (this.isProduct && this.selectedProducts.length < 1) {
                this.emptyProduct = true;
            }

            if (this.isProductCategory && this.selectedProductCategories.length < 1) {
                this.emptyProductCategory = true;
            }

            if (this.selectedPaymentTypes.length < 1) {
                this.emptyPaymentType = true;

                return false;
            }

            return true;
        },


        loadDefaultPaymentTypes() {
            this.selectedPaymentTypes = this.paymentTypes.map((paymentType) => {
                return paymentType.value;
            }).filter((paymentType) => {
                return this.$options.DEFAULT_PAYMENT_TYPES.includes(paymentType);
            });
        },

        getPurchaseOptionId(purchaseId) {
            return `${purchaseId}:${this.purchaseOptionId}`;
        },

        hasGroups(purchaseId) {
            const options = this.products.filter((product) => product.category.value === purchaseId && product.groups);

            return options.length > 0;
        },

        selectPaymentOption() {
            if (!this.isOrderForm) {
                this.selectedOrderForms = [];
            }

            if (!this.isProduct) {
                this.selectedProducts = [];
            }

            if (!this.isProductCategory) {
                this.selectedProductCategories = [];
            }

            this.loadDefaultPaymentTypes();
        },

        closeModal() {
            this.$emit('close-modal');
        },
    },
};

</script>

<style lang="scss" scoped>
.selected-payment-type.multiselect {
    margin-bottom: 0;
}
.payment-summary {
    margin-bottom: $spacing-150;
}

.menu-item .option-subtext {
    font-size:  $font-size-xs;
    color: $color-ink-800;
}

.menu-item.selected .option-subtext {
    color: $color-paper;
}
</style>

<style lang="scss">
.selected-purchase-type .multiselect-single label {
    padding: 0;
}
</style>
